// src/components/ProcessManager/CommercialRegister/ProcessManagerCommercialRegisterResult.tsx
import React from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { IProcessManager } from '../../Interfaces/IProcessManager';
import type { IDocument } from '../../Interfaces/IDocument';
import { DocumentCard } from '../../DocumentPortal/DocumentSortingDialog/DocumentCard';
import {
  formatBackendDate,
  formatBackendDateTime,
} from '../../services/backendTimestamp.service';
import { FAILURE_STATUSES, LAST_CHECK_LABELS } from './lastCheckSummary';

interface Props {
  processManager: IProcessManager & { idProcessManagerType: 20 | 21 };
}

export const ProcessManagerCommercialRegisterResult: React.FC<Props> = ({
  processManager,
}) => {
  const rawData = (processManager as any)?.data;
  const d: any =
    typeof rawData === 'string'
      ? (() => {
          try {
            return JSON.parse(rawData);
          } catch {
            return {};
          }
        })()
      : (rawData ?? {});

  const lastChange = d?.lastChange;
  const dk = d?.dkLastDetection;

  // #1315: Ergebnis-Marker des letzten Laufs (statt Fehler-Mails) sichtbar machen
  const lastCheck = d?.lastCheck;
  const errorLimit = d?.errorLimit;
  const consecutiveErrors = Number(d?.consecutiveErrors ?? 0);

  const idDocument = d?.idDocument != null ? Number(d.idDocument) : undefined;

  const type = processManager.idProcessManagerType;
  const showTradeRegister = type === 20;
  const showShareholderList = type === 21;

  // Die abgelegten Gesellschafterlisten. Platzhalter wie beim Abdruck: den
  // echten Dateinamen liefert erst der Abruf, den Typ kennt DocumentCard ueber
  // idMIMEType.
  const shareholderListDocuments: IDocument[] = Array.isArray(dk?.idDocuments)
    ? (dk.idDocuments as unknown[])
        .map(value => Number(value))
        .filter(value => Number.isFinite(value) && value > 0)
        .map(id => ({
          idDocument: id,
          FileName: `Gesellschafterliste ${id}`,
          Data: undefined,
          idMIMEType: 1,
          Created_at: '',
          idLegalTransaction: null,
          idDocumentDirectory: null,
          perEmail: null,
          isFromTemplate: null,
        }))
    : [];

  const initialDocument: IDocument | undefined = idDocument
    ? {
        idDocument,
        FileName: `Dokument ${idDocument}`,
        Data: undefined,
        idMIMEType: 1,
        Created_at: '',
        idLegalTransaction: null,
        idDocumentDirectory: null,
        perEmail: null,
        isFromTemplate: null,
      }
    : undefined;

  return (
    <Stack spacing={2}>
      {/* Status */}
      <Box>
        <Typography variant='h6' gutterBottom>
          Status
        </Typography>
        <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
          <Chip
            label={`Status: ${processManager.idProcessManagerState === 1 ? 'offen' : 'erledigt'}`}
          />
          <Chip label={`Versuche: ${processManager.Tries ?? 0}`} />
          {processManager.LastTry_at && (
            <Chip
              label={`Letzter Versuch: ${formatBackendDateTime(processManager.LastTry_at)}`}
            />
          )}
          {processManager.Closed_at && (
            <Chip
              color='success'
              label={`Geschlossen: ${formatBackendDateTime(processManager.Closed_at)}`}
            />
          )}
          {processManager.ExitCodeLastTry != null && (
            <Chip label={`ExitCode: ${processManager.ExitCodeLastTry}`} />
          )}
          {lastCheck?.status && (
            <Chip
              color={
                FAILURE_STATUSES.has(lastCheck.status) ? 'error' : 'default'
              }
              label={`Letzte Prüfung: ${
                LAST_CHECK_LABELS[lastCheck.status] ?? lastCheck.status
              } (${formatBackendDateTime(lastCheck.at)})`}
            />
          )}
          {consecutiveErrors > 0 && (
            <Chip
              color='warning'
              label={`Fehlversuche in Folge: ${consecutiveErrors}`}
            />
          )}
          {errorLimit && (
            <Chip
              color='error'
              label={`Wegen wiederholter Fehler geschlossen (${
                errorLimit.after_errors ?? '—'
              } Fehlversuche)`}
            />
          )}
        </Stack>
      </Box>

      {(showTradeRegister || showShareholderList || idDocument) && <Divider />}

      {/* Letzte Handelsregister-Änderung (nur Typ 20) */}
      {showTradeRegister && (
        <Box>
          <Typography variant='h6' gutterBottom>
            Letzte Handelsregister-Änderung (SI)
          </Typography>
          {lastChange ? (
            <Stack spacing={0.5}>
              <Typography variant='body2'>
                Eintragsdatum: {formatBackendDate(lastChange.entry_date)}
              </Typography>
              <Typography variant='body2'>
                Aktenzeichen: {lastChange.aktenzeichen ?? '—'}
              </Typography>
              <Typography variant='body2'>
                Erkannt am: {formatBackendDateTime(lastChange.detected_at)}
              </Typography>
            </Stack>
          ) : (
            <Typography variant='body2'>—</Typography>
          )}
        </Box>
      )}

      {showShareholderList && idDocument && <Divider />}

      {/* Gesellschafterliste (nur Typ 21) */}
      {showShareholderList && (
        <Box>
          <Typography variant='h6' gutterBottom>
            Gesellschafterliste (DK)
          </Typography>
          {dk ? (
            <Stack spacing={0.5}>
              <Typography variant='body2'>
                Neue Dokumente: {dk.new_count ?? 0}
              </Typography>
              {dk.max_doc_date && (
                <Typography variant='body2'>
                  Stand: {formatBackendDate(dk.max_doc_date)}
                </Typography>
              )}
              <Typography variant='body2'>
                Erkannt am: {formatBackendDateTime(dk.detected_at)}
              </Typography>
              {/* QS #24: die gefundenen Listen waren nicht zu oeffnen, obwohl
                  "Neue Dokumente" sie zaehlte — der Handler verwarf die ids der
                  abgelegten Dateien. Eine Karte je Liste, weil ein Lauf mehrere
                  finden kann. */}
              {shareholderListDocuments.length > 0 && (
                <Stack
                  direction='row'
                  spacing={1}
                  flexWrap='wrap'
                  useFlexGap
                  sx={{ pt: 1 }}
                >
                  {shareholderListDocuments.map(doc => (
                    <Box key={doc.idDocument} sx={{ width: 200 }}>
                      <DocumentCard initialDocument={doc} selectable={false} />
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          ) : (
            <Typography variant='body2'>—</Typography>
          )}
        </Box>
      )}

      {idDocument && <Divider />}

      {/* Hinterlegtes Dokument  */}
      {initialDocument ? (
        <Box>
          <Typography variant='h6' gutterBottom>
            Hinterlegtes Dokument (AD)
          </Typography>
          <Box sx={{ maxWidth: 380 }}>
            <DocumentCard
              initialDocument={initialDocument}
              selectable={false}
            />
          </Box>
        </Box>
      ) : null}
    </Stack>
  );
};
